import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useState } from 'react';

import OrderStatusChip from '~components/Order/OrderStatusChip';
import { Job } from '~hooks/useJob/models';
import { useJob } from '~hooks/useJob/useJob';
import { Order } from '~hooks/useOrders/models';
import connection from '~services/connectionModule';
import { useStores } from '~store';
import { extractWaypointData } from '~utils/orders';

import OrderInfo from './OrderInfo';
import { OrderJobList } from './OrderJobList';
import OrderSummaryRow from './OrderSummaryRow';

interface OrderCardProps {
  order: Order;
  onSelectOrder: (order: Order, isExpanded: boolean) => void;
  onSelectJob: (job: Job) => void;
  onViewJobRoute: (job: Job) => void;
  selectedOrder?: Order;
  selectedJob?: Job;
}

const OrderCard = observer(
  ({
    order,
    onSelectOrder,
    onSelectJob,
    onViewJobRoute,
    selectedOrder,
    selectedJob,
  }: OrderCardProps) => {
    const theme = useTheme();
    const { liveMapStoreNew } = useStores();
    const { getJobsByOrder } = useJob();

    const [isExpanded, setIsExpanded] = useState(false);

    // Determine if this order is currently selected
    const isSelected = selectedOrder?.id === order.id;

    // Sync expanded state with selection state
    // Collapse when a different order is selected, expand when this order is selected
    useEffect(() => {
      setIsExpanded(isSelected);
    }, [isSelected]);

    const jobs = liveMapStoreNew.orderJobs.get(order.id);
    const title = useMemo(() => {
      const fragments: string[] = [];

      if (order.dispatchNumber?.name) {
        fragments.push(order.dispatchNumber.name);
      }

      if (order.account?.name) {
        fragments.push(order.account.name);
      }

      const dropOff = extractWaypointData(order.dropOffWayPoints?.[0]);

      if (dropOff) {
        fragments.push(dropOff.name);
      }

      fragments.push(order.orderId);

      return fragments.join(' • ');
    }, [order]);

    const handleOrderCardClick = async () => {
      // Toggle selection: if already selected, deselect; otherwise select this order
      onSelectOrder(order, isSelected);

      // Lazy-load jobs when selecting (not when deselecting)
      if (!isSelected && !jobs) {
        liveMapStoreNew.orderJobsFetchStart(order.id);

        try {
          const response = await getJobsByOrder(order.id);
          liveMapStoreNew.orderJobsFetchEnd(order.id, response);
        } catch (error) {
          connection.handleRequestError(error, t('error_messages.jobs.failed_to_fetch'));
        }
      }
    };

    return (
      <Box
        sx={{
          bgcolor: theme.semantic.surfaceRaised,
          borderRadius: theme.brandV2.borderRadius,
          border: `1px solid ${theme.brandV2.colors.treadGray7}`,
          borderLeft: `2px solid ${isSelected ? theme.palette.info.main : theme.brandV2.colors.treadGray7}`,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          p: 1,
        }}
      >
        <Box
          onClick={handleOrderCardClick}
          sx={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            width: '100%',
            cursor: 'pointer',
            pl: 0.5,
          }}
        >
          <Box sx={{ display: 'flex', width: '100%' }}>
            <KeyboardArrowDown
              htmlColor={theme.brandV2.colors.treadGray6}
              sx={{
                transition: 'transform .2s linear',
                ml: -0.5,
                ...(isExpanded ? {} : { transform: 'rotate(-90deg)' }),
              }}
            />

            <Typography sx={{ fontSize: '13px', fontWeight: 600, pt: '2px', pr: 1 }}>
              {title}
            </Typography>
            <OrderStatusChip order={order} hideDropdownArrow sx={{ ml: 'auto' }} />
          </Box>

          <OrderInfo order={order} />
        </Box>
        <Collapse in={isExpanded}>
          <OrderSummaryRow order={order} />
          <OrderJobList
            orderId={order.id}
            selectedJobId={selectedJob?.id}
            onSelect={onSelectJob}
            onViewRoute={onViewJobRoute}
          />
        </Collapse>
      </Box>
    );
  },
);

export default OrderCard;
